import { useState, useCallback, useEffect } from 'react';

const ANTD_PAGINATION_SELECTOR = 'button';

export const usePaginationRoving = () => {
    // We request roving for buttons, and strip the noise from the LIs
    return useIndexRoving(ANTD_PAGINATION_SELECTOR, 'li');
};

export const useIndexRoving = (selector = 'button, input', removeTabIndexFrom = '') => {
    // 1. Use state to store the node so it triggers updates
    const [container, setContainer] = useState(null);

    // 2. The callback ref you'll pass to the element
    const containerRef = useCallback((node) => {
        if (node !== null) {
            setContainer(node);
        }
    }, []);

    // 3. Extract the logic into a sync function so it can be triggered by events
    const sync = useCallback(() => {
        if (!container) return;

        const items = Array.from(container.querySelectorAll(selector))
            .filter(el => !el.disabled && el.offsetParent !== null); // basic visibility check

        if (removeTabIndexFrom !== '') {
            const removeItems = Array.from(container.querySelectorAll(removeTabIndexFrom));
            removeItems.forEach((item) => {
                item.removeAttribute('tabindex');
            });
        }

        // Try to find the currently active page (for pagination), default to 0
        const activeIdx = items.findIndex(el =>
            el.classList.contains('active-page-style-class') ||
            el.getAttribute('aria-current') === 'page'
        );
        const entryIdx = activeIdx !== -1 ? activeIdx : 0;

        items.forEach((item, idx) => {
            item.setAttribute('tabindex', idx === entryIdx ? '0' : '-1');
        });
    }, [container, selector, removeTabIndexFrom]);

    // 4. Run on mount (when container is captured)
    useEffect(() => {
        sync();
    }, [sync]);

    // 5. Interaction triggers (The "Observer-Free" solution)
    // Run sync when focus enters the container to ensure tabIndex is ready
    const onFocus = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            sync();
        }
    };

    // Run sync on click, using setTimeout to wait for AntD to finish re-rendering the DOM
    const onClick = () => setTimeout(sync, 0);

    const onKeyDown = (e) => {
        if (!container) return;

        const hasPopup = e.target.getAttribute('aria-haspopup');
        const role = e.target.getAttribute('role');
        const isNativeSelect = e.target.tagName === 'SELECT';

        const isDropdown = role === 'combobox' || (hasPopup && hasPopup !== 'false') || isNativeSelect;
        const isArrowKey = e.key === 'ArrowUp' || e.key === 'ArrowDown';

        if (isDropdown && isArrowKey) {
            return; // Exit and let the dropdown handle its own navigation
        }

        const items = Array.from(container.querySelectorAll(selector))
            .filter(el => !el.disabled && el.offsetParent !== null);

        const currIdx = items.indexOf(document.activeElement);
        if (currIdx === -1) return;

        const navKeys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!navKeys.includes(e.key)) return;

        e.preventDefault();

        let nextIdx = currIdx;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (currIdx + 1) % items.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = (currIdx - 1 + items.length) % items.length;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = items.length - 1;

        items.forEach((item, idx) => item.setAttribute('tabindex', idx === nextIdx ? '0' : '-1'));

        // Strip tabindex from parent <li/> dynamically just in case AntD tried to sneak it back in
        if (removeTabIndexFrom !== '') {
            items[nextIdx].closest(removeTabIndexFrom)?.removeAttribute('tabindex');
        }

        items[nextIdx].focus();
    };

    return { ref: containerRef, onKeyDown, onFocus, onClick };
};